import { createTheme, lightThemePrimitives, LightTheme } from "baseui";
import colors from "./colors";
import { DeepPartial } from "../../sharedTypes";

// Defaults are from https://github.com/uber/baseweb/blob/master/src/theme.ts

export const primitives: Partial<typeof lightThemePrimitives> = {
  primaryFontFamily: '"IBM Plex Sans", "Helvetica Neue", Arial, sans-serif',
  // Primary Palette
  primaryA: colors.cyan.cyan500,
  primaryB: colors.cyan.cyan200,
  primary: colors.cyan.cyan500,
  primary50: colors.cyan.cyan50,
  primary100: colors.cyan.cyan100,
  primary200: colors.cyan.cyan200,
  primary300: colors.cyan.cyan300,
  primary400: colors.cyan.cyan400,
  primary500: colors.cyan.cyan500,
  primary600: colors.cyan.cyan600,
  primary700: colors.cyan.cyan700,

  // Accent Palette
  accent: colors.purple.purple400,
  accent50: colors.purple.purple50,
  accent100: colors.purple.purple100,
  accent200: colors.purple.purple200,
  accent300: colors.purple.purple300,
  accent400: colors.purple.purple400,
  accent500: colors.purple.purple500,
  accent600: colors.purple.purple600,
  accent700: colors.purple.purple700,

  // Alert Palette
  negative: colors.red.red500,
  negative50: colors.red.red50,
  negative100: colors.red.red100,
  negative200: colors.red.red200,
  negative300: colors.red.red300,
  negative400: colors.red.red400,
  negative500: colors.red.red500,
  negative600: colors.red.red600,
  negative700: colors.red.red700,

  // Warning Palette
  warning: colors.yellow.yellow500,
  warning50: colors.yellow.yellow50,
  warning100: colors.yellow.yellow100,
  warning200: colors.yellow.yellow200,
  warning300: colors.yellow.yellow300,
  warning400: colors.yellow.yellow400,
  warning500: colors.yellow.yellow500,
  warning600: colors.yellow.yellow600,
  warning700: colors.yellow.yellow700,

  // Success Palette
  positive: colors.green.green500,
  positive50: colors.green.green50,
  positive100: colors.green.green100,
  positive200: colors.green.green200,
  positive300: colors.green.green300,
  positive400: colors.green.green400,
  positive500: colors.green.green500,
  positive600: colors.green.green600,
  positive700: colors.green.green700,

  // Monochrome Palette
  mono100: colors.gray.gray50,
  mono200: colors.gray.gray100,
  mono300: colors.gray.gray200,
  mono400: colors.gray.gray300,
  mono500: colors.gray.gray400,
  mono600: colors.gray.gray500,
  mono700: colors.gray.gray600,
  mono800: colors.gray.gray700,
  mono900: colors.gray.gray800,
  mono1000: colors.gray.gray900,
};

const overrides: DeepPartial<typeof LightTheme> = {
  colors: {
    // Border
    borderTransparent: `8% ${primitives.mono1000}`,
    borderSelected: primitives.mono1000,
    borderInverseTransparent: `20% ${primitives.primaryB}`,

    // Core - Background
    backgroundPrimary: colors.white,
    backgroundSecondary: primitives.mono300,
    backgroundTertiary: primitives.mono100,
    backgroundInversePrimary: primitives.primaryA,
    backgroundInverseSecondary: primitives.mono1000,
    backgroundAccent: primitives.accent,
    backgroundNegative: primitives.negative,
    backgroundWarning: primitives.warning200,
    backgroundPositive: primitives.positive,

    // Core extensions - Background
    backgroundStateDisabled: primitives.mono100,
    backgroundOverlayDark: `30% ${colors.black}`,
    backgroundOverlayLight: `8% ${colors.black}`,
    backgroundAccentLight: primitives.accent50,
    backgroundNegativeLight: primitives.negative50,
    backgroundWarningLight: primitives.warning50,
    backgroundPositiveLight: primitives.positive50,
    backgroundAlwaysDark: colors.black,
    backgroundAlwaysLight: colors.white,

    // Core - Content
    contentPrimary: primitives.mono1000,
    contentSecondary: primitives.mono800,
    contentTertiary: primitives.mono600,
    contentInversePrimary: colors.white,
    contentInverseSecondary: primitives.mono300,
    contentInverseTertiary: primitives.mono400,
    contentNegative: primitives.negative,
    contentWarning: primitives.warning,
    contentPositive: primitives.positive,

    // Core extensions - Content
    contentStateDisabled: primitives.mono500,
    contentOnColor: colors.white,
    contentAccent: primitives.accent,

    // Core - Border
    borderOpaque: primitives.mono300,
    borderInverseOpaque: primitives.mono800,
    borderInverseSelected: colors.white,

    // Core extensions - Border
    borderStateDisabled: primitives.mono500,
    borderAccent: primitives.accent,
    borderAccentLight: primitives.accent300,
    borderNegative: primitives.negative300,
    borderWarning: primitives.warning300,
    borderPositive: primitives.positive300,

    // Button
    buttonPrimaryFill: primitives.primaryA,
    buttonPrimaryHover: primitives.primary600,
    buttonPrimaryActive: primitives.primary400,
    buttonPrimarySpinnerForeground: primitives.accent,
    buttonPrimarySpinnerBackground: colors.white,

    buttonSecondaryFill: primitives.mono300,
    buttonSecondaryText: primitives.mono1000,
    buttonSecondaryHover: primitives.mono400,
    buttonSecondaryActive: primitives.mono500,
    buttonSecondarySpinnerForeground: primitives.accent,
    buttonSecondarySpinnerBackground: colors.white,

    buttonTertiaryText: primitives.mono1000,
    buttonTertiaryHover: primitives.mono100,
    buttonTertiaryActive: primitives.mono400,
    buttonTertiarySpinnerForeground: primitives.accent,
    buttonTertiarySpinnerBackground: primitives.mono100,

    buttonDisabledText: primitives.mono500,
    buttonDisabledFill: primitives.primary50,
    // End Button

    // Progress Bar
    progressbarTrackFill: primitives.accent50,

    // Progress Steps
    progressStepsCompletedText: colors.white,
    progressStepsCompletedFill: primitives.mono1000,
    progressStepsActiveText: colors.white,
    progressStepsActiveFill: primitives.mono1000,

    //Input
    inputTextDisabled: primitives.mono500,
    inputFillDisabled: primitives.mono100,
    inputPlaceholderDisabled: primitives.mono500,
    inputPlaceholder: primitives.mono600,
    inputBorder: primitives.mono600,
    inputFill: primitives.mono100,
    inputFillActive: primitives.mono100,

    // Datepicker
    calendarBackground: colors.white,
    calendarHeaderForeground: primitives.mono1000,
    calendarDayBackgroundSelectedHighlighted: primitives.mono1000,
    calendarDayBackgroundPseudoSelectedHighlighted: primitives.mono100,

    //Select
    menuFill: colors.white,
    menuFillHover: primitives.mono100,
    menuFontDisabled: primitives.mono600,

    //Radio and Checkbox
    toggleFillChecked: primitives.accent,
    tickFillSelected: primitives.accent,
    tickFillSelectedHover: primitives.accent500,
    tickFillSelectedHoverActive: primitives.accent700,

    // Table
    tableHeadBackgroundColor: colors.white,
    tableBackground: colors.white,
    tableStripedBackground: primitives.mono100,

    // Link text
    linkText: primitives.primary500,
    linkHover: primitives.primary600,
    linkVisited: primitives.primary500,
    linkActive: primitives.primary600,

    // Tag
    tagNeutralSolidBackground: primitives.mono1000,
    tagNeutralOutlinedFont: primitives.mono1000,
    tagNeutralOutlinedBackground: primitives.mono300,
    tagNeutralOutlinedDisabled: primitives.mono100,
    tagNeutralFontDisabled: primitives.mono300,

    tagPrimarySolidBackground: primitives.primary400,
    tagPrimaryOutlinedFont: primitives.primary500,
    tagPrimaryOutlinedBackground: primitives.primary200,
    tagPrimaryOutlinedDisabled: primitives.primary100,
    tagPrimaryFontDisabled: primitives.primary100,

    tagNegativeSolidBackground: primitives.negative600,
    tagNegativeOutlinedFont: primitives.negative600,
    tagNegativeOutlinedBackground: primitives.negative200,
    tagNegativeOutlinedDisabled: primitives.negative100,
    tagNegativeFontDisabled: primitives.negative100,

    tagAccentSolidBackground: primitives.accent400,
    tagAccentOutlinedFont: primitives.accent400,
    tagAccentOutlinedBackground: primitives.accent200,
    tagAccentOutlinedDisabled: primitives.accent100,
    tagAccentFontDisabled: primitives.accent100,

    tagWarningSolidBackground: primitives.warning400,
    tagWarningSolidFont: primitives.mono1000,
    tagWarningOutlinedFont: primitives.warning700,
    tagWarningOutlinedBackground: primitives.warning300,
    tagWarningOutlinedDisabled: primitives.warning100,
    tagWarningFontDisabled: primitives.warning100,

    tagPositiveSolidBackground: primitives.positive400,
    tagPositiveOutlinedFont: primitives.positive600,
    tagPositiveOutlinedBackground: primitives.positive200,
    tagPositiveOutlinedDisabled: primitives.positive100,
    tagPositiveFontDisabled: primitives.positive100,
  },
  typography: {
    // .ParagraphXSmall
    font100: {
      fontFamily: primitives.primaryFontFamily,
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "20px",
    },
    // .LabeLXSmall
    font150: {
      fontFamily: primitives.primaryFontFamily,
      fontSize: "12px",
      fontWeight: 500,
      lineHeight: "16px",
    },
    // .ParagraphSmall
    font200: {
      fontFamily: primitives.primaryFontFamily,
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "20px",
    },
    // .LabelSmall
    font250: {
      fontFamily: primitives.primaryFontFamily,
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: "16px",
    },
    // .ParagraphMedium
    font300: {
      fontFamily: primitives.primaryFontFamily,
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "24px",
    },
    // .LabelMedium
    font350: {
      fontFamily: primitives.primaryFontFamily,
      fontSize: "16px",
      fontWeight: 500,
      lineHeight: "20px",
    },
    // .ParagraphLarge
    font400: {
      fontFamily: primitives.primaryFontFamily,
      fontSize: "18px",
      fontWeight: 400,
      lineHeight: "28px",
    },
    // .LabelLarge
    font450: {
      fontFamily: primitives.primaryFontFamily,
      fontSize: "18px",
      fontWeight: 500,
      lineHeight: "24px",
    },
    // .HeadingXSmall
    font550: {
      fontFamily: primitives.primaryFontFamily,
      fontSize: "20px",
      fontWeight: 600,
      lineHeight: "28px",
    },
    // .HeadingSmall
    font650: {
      fontFamily: primitives.primaryFontFamily,
      fontSize: "25px",
      fontWeight: 600,
      lineHeight: "32px",
    },
    // .HeadingMedium
    font750: {
      fontFamily: primitives.primaryFontFamily,
      fontSize: "29px",
      fontWeight: 600,
      lineHeight: "36px",
    },
    // .HeadingLarge
    font850: {
      fontFamily: primitives.primaryFontFamily,
      fontSize: "32px",
      fontWeight: 600,
      lineHeight: "40px",
    },
    // .HeadingXLarge
    font950: {
      fontFamily: primitives.primaryFontFamily,
      fontSize: "37px",
      fontWeight: 600,
      lineHeight: "45px",
    },
    // .HeadingXXLarge
    font1050: {
      fontFamily: primitives.primaryFontFamily,
      fontSize: "41px",
      fontWeight: 600,
      lineHeight: "53px",
    },
    // .DisplayXSmall
    font1150: {
      fontFamily: primitives.primaryFontFamily,
      fontSize: "41px",
      fontWeight: 400,
      lineHeight: "51px",
    },
    // .DisplaySmall
    font1250: {
      fontFamily: primitives.primaryFontFamily,
      fontSize: "46px",
      fontWeight: 400,
      lineHeight: "54px",
    },
    // .DisplayMedium
    font1350: {
      fontFamily: primitives.primaryFontFamily,
      fontSize: "52px",
      fontWeight: 400,
      lineHeight: "64px",
    },
    // .DisplayLarge
    font1450: {
      fontFamily: primitives.primaryFontFamily,
      fontSize: "58px",
      fontWeight: 400,
      lineHeight: "73px",
    },
  },
  grid: {
    margins: [0, 0, 0],
    maxWidth: 9999, //This is necesarry to force the grid to be 100% wide
    gaps: [24, 24, 24],
  },
};
const baseuiTheme = createTheme(primitives, overrides);

export type Theme = typeof baseuiTheme;

export default baseuiTheme;
